import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getPlayersStatus } from './utils/dbUtils';
import { readSaved, save } from '../gameDeck';
import { ACTIVE_ROOM, CONTROLLER_TAB, displayKey, displayRequest, publicDisplay } from '../creativeDisplay';
import { identityFor } from '../playerIdentity';

const DisplayContext = createContext({ status: '', paired: false });
export function CreativeDisplayProvider({ children }) {
  const location = useLocation();
  const match = /^\/creative-mode\/(\d{4})\/(share|enter-prompts|waiting-lobby|play|results)$/.exec(location.pathname);
  const code = match?.[1], route = match?.[2];
  const currentRoute = useRef(route); currentRoute.current = route;
  const tabId = useRef(CONTROLLER_TAB);
  const [state, setState] = useState({ status: '', paired: false });
  useEffect(() => {
    if (!code || !readSaved(code + '_data', {})?.isHost) { setState({ status: '', paired: false }); return; }
    const ownerKey = `diyt-controller-${code}`;
    if (readSaved(displayKey(code), null)?.paired) setState({ status: 'Connecting to TV…', paired: true });
    let stopped = false, busy = false, players = [], previous = '', lastSent = 0;
    const claim = () => { if (document.visibilityState === 'visible') { save(ownerKey, tabId.current); publish(); } };
    save(ownerKey, tabId.current);
    async function publish() {
      if (stopped || busy || document.visibilityState !== 'visible' || readSaved(ownerKey, '') !== tabId.current) return;
      const channel = readSaved(displayKey(code), null);
      if (!channel?.paired) return;
      const snapshot = publicDisplay(code, currentRoute.current, readSaved(code + '_data', {}), players);
      const serialized = JSON.stringify(snapshot);
      if (serialized === previous && Date.now() - lastSent < 15000) return;
      busy = true;
      try {
        const revision = Math.max(Date.now(), (channel.revision || 0) + 1);
        const result = await displayRequest('publish', { id: channel.id, token: channel.token, revision, snapshot });
        previous = serialized; lastSent = Date.now();
        save(displayKey(code), { ...readSaved(displayKey(code), channel), revision: Math.max(result.revision, revision) });
        if (!stopped) setState({ status: result.connected ? 'TV paired' : 'Connect a TV', paired: result.connected });
      } catch (error) { if (!stopped) setState({ status: error.status === 401 ? 'TV connection expired' : 'Reconnecting to TV…', paired: true }); }
      finally { busy = false; }
    }
    const unsubscribe = getPlayersStatus(code).subscribe(list => { players = list.map(p => ({ name: p.name, isReady: p.isReady, ...identityFor(p) })); publish(); });
    const timer = setInterval(publish, 1500);
    window.addEventListener('creative-turn', publish);
    window.addEventListener('focus', claim);
    document.addEventListener('visibilitychange', claim);
    publish();
    return () => { stopped = true; clearInterval(timer); unsubscribe(); window.removeEventListener('creative-turn', publish); window.removeEventListener('focus', claim); document.removeEventListener('visibilitychange', claim); };
  }, [code]);
  useEffect(() => { if (code && readSaved(code + '_data', {})?.isHost) { save(ACTIVE_ROOM, { code, route }); window.dispatchEvent(new Event('creative-turn')); } }, [code, route]);
  return <DisplayContext.Provider value={state}>{children}</DisplayContext.Provider>;
}
export function CreativeTVLink({ code }) {
  const { paired, status } = useContext(DisplayContext);
  if (!readSaved(code + '_data', {})?.isHost) return null;
  return <Link className="creative-tv-link" to={`/creative-mode/connect?room=${code}`}>{paired ? status : 'Connect a TV'}<span aria-hidden="true">›</span></Link>;
}
